import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  computed,
  inject,
  signal,
  type OnInit,
} from '@angular/core';
import { NgpSelectPortal } from 'ng-primitives/select';
import { environment } from '../../../environments/environment';
import {
  UiBadge,
  UiButton,
  UiEmptyText,
  UiInput,
  UiOptionRow,
  UiPanelCard,
  UiSelect,
  UiSelectDropdown,
  UiSelectOption,
} from '../../shared/ui';

declare global {
  interface Window {
    FB?: {
      init: (config: Record<string, unknown>) => void;
      login: (
        callback: (response: FBLoginResponse) => void,
        options: Record<string, unknown>,
      ) => void;
    };
    fbAsyncInit?: () => void;
  }
}

function isMessageType(value: unknown): value is MessageType {
  return (
    value === 'text'
    || value === 'image'
    || value === 'video'
    || value === 'audio'
    || value === 'document'
    || value === 'sticker'
  );
}

interface FBLoginResponse {
  authResponse?: {
    code?: string;
    accessToken?: string;
    userID?: string;
  } | null;
  status?: string;
}

interface SessionEvent {
  at: string;
  raw: string;
  parsed?: Record<string, unknown>;
}

interface ConversationSummary {
  id: string;
  contactId: string;
  status: string;
  lastMessageAt: string | null;
  origin: string;
}

interface MessageEntry {
  id: string;
  senderType: string;
  senderId: string | null;
  type: string;
  contentText: string;
  sentAt: string;
  deliveryStatus: string | null;
}

type MessageType = 'text' | 'image' | 'video' | 'audio' | 'document' | 'sticker';

const FB_SDK_SCRIPT_ID = 'facebook-jssdk';
const FB_SDK_SRC = 'https://connect.facebook.net/en_US/sdk.js';
const FB_GRAPH_VERSION = 'v25.0';

const META_APP_ID = environment.metaAppId;
const META_CONFIG_ID = environment.metaConfigId;

@Component({
  selector: 'app-whatsapp-signup-test-page',
  template: `
    <main class="mx-auto flex min-h-dvh w-full max-w-4xl flex-col gap-6 py-10 [padding-inline:var(--shell-pad-x)]">
      <header>
        <p class="text-xs font-semibold uppercase tracking-wide text-accent">Meta WhatsApp</p>
        <h1 class="mt-2 text-2xl font-semibold text-ink">Embedded Signup — página de teste</h1>
        <p class="mt-3 max-w-2xl text-sm leading-6 text-muted">
          Carrega o SDK do Facebook, escuta o <code>postMessage</code> com os dados da sessão e
          dispara o flow do Cadastro Incorporado. Use o botão abaixo para iniciar o fluxo.
        </p>
      </header>

      <section appUiPanelCard>
        <div class="flex flex-wrap items-center gap-3">
          <button
            appButton
            variant="primary"
            type="button"
            (click)="launchWhatsAppSignup()"
            [disabled]="!sdkReady()"
          >
            Login with Facebook
          </button>
          <span class="text-xs text-muted">
            SDK:
            <span [class.text-success]="sdkReady()" [class.text-warning]="!sdkReady()">
              {{ sdkReady() ? 'pronto' : 'carregando…' }}
            </span>
          </span>
        </div>
      </section>

      <section appUiPanelCard>
        <p class="text-sm font-semibold text-ink">Resposta do FB.login</p>
        @if (loginResponse(); as resp) {
          <pre class="mt-3 overflow-auto rounded-md bg-soft p-3 text-xs text-ink">{{ resp }}</pre>
          @if (signupCode()) {
            <p class="mt-3 text-sm text-ink">
              <span class="font-semibold">Code:</span>
              <code>{{ signupCode() }}</code>
            </p>
          }
        } @else {
          <p class="mt-3 text-sm text-muted">Aguardando conclusão do flow…</p>
        }
      </section>

      <section appUiPanelCard>
        <p class="text-sm font-semibold text-ink">Eventos de sessão (postMessage)</p>
        <p class="mt-1 text-xs text-muted">
          Origem aceita: <code>https://www.facebook.com</code> · Tipo:
          <code>WA_EMBEDDED_SIGNUP</code>
        </p>
        @if (sessionEvents().length === 0) {
          <p appUiEmptyText>Nenhum evento recebido ainda.</p>
        } @else {
          <ul class="mt-3 flex flex-col gap-3">
            @for (ev of sessionEvents(); track ev.at) {
              <li appUiPanelCard>
                <p class="text-xs text-muted">{{ ev.at }}</p>
                <pre class="mt-1 overflow-auto text-xs text-ink">{{ ev.raw }}</pre>
              </li>
            }
          </ul>
        }
      </section>

      <section appUiPanelCard>
        <p class="text-sm font-semibold text-ink">Identificações extraídas</p>
        <dl class="mt-3 grid gap-2 text-sm sm:grid-cols-3">
          <div>
            <dt class="text-xs uppercase tracking-wide text-muted">WABA ID</dt>
            <dd class="text-ink">{{ wabaId() || '—' }}</dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-muted">Phone Number ID</dt>
            <dd class="text-ink">{{ phoneNumberId() || '—' }}</dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-muted">Meta Business ID</dt>
            <dd class="text-ink">{{ metaBusinessId() || '—' }}</dd>
          </div>
        </dl>
      </section>

      <section appUiPanelCard>
        <p class="text-sm font-semibold text-ink">Resultado do backend</p>
        @if (connecting()) {
          <p class="mt-3 text-sm text-muted">Conectando…</p>
        } @else if (connectResult(); as result) {
          <pre class="mt-3 overflow-auto rounded-md bg-soft p-3 text-xs text-ink">{{ result }}</pre>
        } @else {
          <p class="mt-3 text-sm text-muted">Aguardando POST ao backend…</p>
        }
      </section>

      <hr class="border-line" />

      <section appUiPanelCard>
        <div class="flex items-center justify-between">
          <p class="text-sm font-semibold text-ink">Conversas</p>
          <button
            type="button"
            (click)="loadConversations()"
            appButton
            variant="secondary"
            size="sm"
          >
            {{ loadingConversations() ? 'Carregando…' : 'Atualizar' }}
          </button>
        </div>
        @if (conversations().length === 0 && !loadingConversations()) {
          <p appUiEmptyText>
            Nenhuma conversa. Envie uma mensagem WhatsApp pro número conectado e clique Atualizar.
          </p>
        } @else {
          <ul class="mt-3 flex flex-col gap-2">
            @for (conv of conversations(); track conv.id) {
              <li>
                <button
                  appUiOptionRow
                  type="button"
                  (click)="selectConversation(conv.id)"
                  [selected]="selectedConversationId() === conv.id"
                  class="w-full"
                >
                  <div class="flex items-center justify-between text-xs">
                    <span class="font-mono text-ink">{{ conv.id.slice(0, 8) }}…</span>
                    <span appBadge [tone]="conversationStatusTone(conv.status)" size="xs">{{ conv.status }}</span>
                  </div>
                  <p class="mt-1 text-xs text-muted">
                    {{ conv.origin }} · {{ conv.lastMessageAt || '—' }}
                  </p>
                </button>
              </li>
            }
          </ul>
        }
      </section>

      @if (selectedConversationId()) {
        <section appUiPanelCard>
          <div class="flex items-center justify-between">
            <p class="text-sm font-semibold text-ink">Mensagens</p>
            <button
              type="button"
              (click)="loadMessages()"
            appButton
            variant="secondary"
            size="sm"
            >
              {{ loadingMessages() ? 'Carregando…' : 'Atualizar' }}
            </button>
          </div>

          <div class="mt-3 flex max-h-96 flex-col gap-2 overflow-y-auto">
            @for (msg of messages(); track msg.id) {
              <div
                class="rounded-md p-3 text-sm"
                [class.bg-info-soft]="msg.senderType === 'contact'"
                [class.ml-0]="msg.senderType === 'contact'"
                [class.mr-12]="msg.senderType === 'contact'"
                [class.bg-success-soft]="msg.senderType !== 'contact'"
                [class.ml-12]="msg.senderType !== 'contact'"
                [class.mr-0]="msg.senderType !== 'contact'"
              >
                <div class="flex items-center justify-between text-[10px] text-muted">
                  <span class="font-semibold uppercase">{{ msg.senderType }}</span>
                  <span>{{ msg.sentAt }}</span>
                </div>
                <p class="mt-1 text-ink">{{ msg.contentText || '(sem texto)' }}</p>
                @if (msg.deliveryStatus) {
                  <p
                    class="mt-0.5 text-[10px] font-semibold uppercase"
                    [class.text-warning]="msg.deliveryStatus === 'pending'"
                    [class.text-info]="msg.deliveryStatus === 'sent'"
                    [class.text-success]="
                      msg.deliveryStatus === 'delivered' || msg.deliveryStatus === 'read'
                    "
                    [class.text-danger]="msg.deliveryStatus === 'failed'"
                  >
                    {{ msg.deliveryStatus }}
                  </p>
                }
              </div>
            }
            @if (messages().length === 0 && !loadingMessages()) {
              <p appUiEmptyText>Nenhuma mensagem nesta conversa.</p>
            }
          </div>

          <div class="mt-4 flex flex-col gap-2">
            <div class="flex gap-2">
              <button
                appSelect
                type="button"
                class="max-w-48"
                aria-label="Tipo da mensagem"
                [ngpSelectValue]="messageType()"
                (ngpSelectValueChange)="setMessageType($event)"
              >
                <span>{{ messageTypeLabel() }}</span>
                <div *ngpSelectPortal appSelectDropdown>
                  <div appSelectOption [ngpSelectOptionValue]="'text'">Texto</div>
                  <div appSelectOption [ngpSelectOptionValue]="'image'">Imagem (URL)</div>
                  <div appSelectOption [ngpSelectOptionValue]="'video'">Vídeo (URL)</div>
                  <div appSelectOption [ngpSelectOptionValue]="'audio'">Áudio (URL)</div>
                  <div appSelectOption [ngpSelectOptionValue]="'document'">Documento (URL)</div>
                  <div appSelectOption [ngpSelectOptionValue]="'sticker'">Sticker (URL)</div>
                </div>
              </button>
              <input
                appInput
                type="text"
                [value]="recipientOverride()"
                (input)="recipientOverride.set($any($event.target).value)"
                placeholder="to override (opcional, E.164 sem +)"
                class="flex-1 text-xs"
              />
            </div>
            @if (messageType() === 'text') {
              <input
                appInput
                type="text"
                [value]="draftMessage()"
                (input)="draftMessage.set($any($event.target).value)"
                (keydown.enter)="sendMessage()"
                placeholder="Digite uma mensagem de teste…"
                class="flex-1"
              />
            } @else {
              <input
                appInput
                type="text"
                [value]="draftMediaUrl()"
                (input)="draftMediaUrl.set($any($event.target).value)"
                placeholder="URL pública HTTPS do arquivo"
                class="flex-1"
              />
              <input
                appInput
                type="text"
                [value]="draftMessage()"
                (input)="draftMessage.set($any($event.target).value)"
                placeholder="Caption (opcional)"
                class="flex-1"
              />
            }
            <button
              appButton
              variant="primary"
              type="button"
              (click)="sendMessage()"
              [disabled]="sendingMessage() || !canSend()"
              class="self-end"
            >
              {{ sendingMessage() ? 'Enviando…' : 'Enviar' }}
            </button>
          </div>
          @if (sendError()) {
            <p class="mt-2 text-xs text-danger">{{ sendError() }}</p>
          }
        </section>
      }
    </main>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgpSelectPortal,
    UiBadge,
    UiButton,
    UiEmptyText,
    UiInput,
    UiOptionRow,
    UiPanelCard,
    UiSelect,
    UiSelectDropdown,
    UiSelectOption,
  ],
})
export class WhatsappSignupTestPage implements OnInit {
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  readonly sdkReady = signal(false);
  readonly loginResponse = signal<string | null>(null);
  readonly signupCode = signal<string | null>(null);
  readonly sessionEvents = signal<SessionEvent[]>([]);
  readonly connectResult = signal<string | null>(null);
  readonly connecting = signal(false);

  readonly wabaId = computed(() => this.extractFinishField('waba_id'));
  readonly phoneNumberId = computed(() => this.extractFinishField('phone_number_id'));
  readonly metaBusinessId = computed(() => this.extractFinishField('business_id'));

  readonly conversations = signal<ConversationSummary[]>([]);
  readonly loadingConversations = signal(false);
  readonly selectedConversationId = signal<string | null>(null);
  readonly messages = signal<MessageEntry[]>([]);
  readonly loadingMessages = signal(false);
  readonly draftMessage = signal('');
  readonly draftMediaUrl = signal('');
  readonly messageType = signal<MessageType>('text');
  readonly recipientOverride = signal('');
  readonly sendingMessage = signal(false);
  readonly sendError = signal<string | null>(null);

  readonly canSend = computed(() => {
    if (this.messageType() === 'text') return Boolean(this.draftMessage().trim());
    return Boolean(this.draftMediaUrl().trim());
  });

  private pollIntervalId: ReturnType<typeof setInterval> | null = null;

  ngOnInit(): void {
    this.loadFbSdk();
  }

  messageTypeLabel(): string {
    const labels: Record<MessageType, string> = {
      text: 'Texto',
      image: 'Imagem (URL)',
      video: 'Vídeo (URL)',
      audio: 'Áudio (URL)',
      document: 'Documento (URL)',
      sticker: 'Sticker (URL)',
    };
    return labels[this.messageType()];
  }

  conversationStatusTone(status: string): 'success' | 'warning' | 'neutral' {
    if (status === 'open') return 'success';
    if (status === 'pending') return 'warning';
    return 'neutral';
  }

  setMessageType(value: unknown): void {
    if (isMessageType(value)) this.messageType.set(value);
  }

  @HostListener('window:message', ['$event'])
  onWindowMessage(event: MessageEvent): void {
    if (event.origin !== 'https://www.facebook.com') return;
    const raw = typeof event.data === 'string' ? event.data : JSON.stringify(event.data);
    let parsed: Record<string, unknown> | undefined;
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = undefined;
    }
    if (parsed?.['type'] !== 'WA_EMBEDDED_SIGNUP') return;

    this.sessionEvents.update((list) => [...list, { at: new Date().toISOString(), raw, parsed }]);
  }

  launchWhatsAppSignup(): void {
    const fb = window.FB;
    if (!fb) {
      this.loginResponse.set('FB SDK ainda não carregado.');
      return;
    }
    fb.login(
      (response) => {
        this.loginResponse.set(JSON.stringify(response, null, 2));
        const code = response?.authResponse?.code;
        if (code) {
          this.signupCode.set(code);
          this.postConnectToBackend(code);
        }
      },
      {
        config_id: META_CONFIG_ID,
        response_type: 'code',
        override_default_response_type: true,
        extras: { version: 'v4' },
      },
    );
  }

  async loadConversations(): Promise<void> {
    this.loadingConversations.set(true);
    try {
      const res = await fetch(`${environment.apiUrl}/v1/conversations?limit=20`, {
        credentials: 'include',
      });
      const body = await res.json();
      const data = (body.data ?? []) as Array<Record<string, unknown>>;
      this.conversations.set(
        data.map((c) => ({
          id: c['id'] as string,
          contactId: c['contactId'] as string,
          status: c['status'] as string,
          lastMessageAt: (c['lastMessageAt'] as string) ?? null,
          origin: c['origin'] as string,
        })),
      );
    } catch {
      this.conversations.set([]);
    } finally {
      this.loadingConversations.set(false);
    }
  }

  selectConversation(id: string): void {
    this.selectedConversationId.set(id);
    this.messages.set([]);
    this.sendError.set(null);
    this.loadMessages();
    this.ensurePolling();
  }

  private ensurePolling(): void {
    if (this.pollIntervalId) return;
    this.pollIntervalId = setInterval(() => {
      const hasPending = this.messages().some(
        (m) => m.deliveryStatus === 'pending' || m.deliveryStatus === 'sent',
      );
      if (hasPending && !this.loadingMessages()) {
        this.loadMessages();
      }
    }, 3000);
    this.destroyRef.onDestroy(() => {
      if (this.pollIntervalId) clearInterval(this.pollIntervalId);
      this.pollIntervalId = null;
    });
  }

  async loadMessages(): Promise<void> {
    const convId = this.selectedConversationId();
    if (!convId) return;
    this.loadingMessages.set(true);
    try {
      const res = await fetch(
        `${environment.apiUrl}/v1/conversations/${convId}/messages?limit=50&sort=sent_at_asc`,
        { credentials: 'include' },
      );
      const body = await res.json();
      const data = (body.data ?? []) as Array<Record<string, unknown>>;
      this.messages.set(
        data.map((m) => ({
          id: m['id'] as string,
          senderType: m['senderType'] as string,
          senderId: (m['senderId'] as string) ?? null,
          type: m['type'] as string,
          contentText: m['contentText'] as string,
          sentAt: m['sentAt'] as string,
          deliveryStatus: (m['deliveryStatus'] as string) ?? null,
        })),
      );
    } catch {
      this.messages.set([]);
    } finally {
      this.loadingMessages.set(false);
    }
  }

  async sendMessage(): Promise<void> {
    const convId = this.selectedConversationId();
    if (!convId || !this.canSend()) return;

    this.sendingMessage.set(true);
    this.sendError.set(null);
    try {
      const body = this.buildSendBody();
      const res = await fetch(`${environment.apiUrl}/v1/conversations/${convId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        this.sendError.set(
          `${res.status}: ${(err as Record<string, unknown>)['message'] ?? JSON.stringify(err) ?? res.statusText}`,
        );
        return;
      }
      this.draftMessage.set('');
      this.draftMediaUrl.set('');
      await this.loadMessages();
    } catch (err) {
      this.sendError.set(err instanceof Error ? err.message : String(err));
    } finally {
      this.sendingMessage.set(false);
    }
  }

  private buildSendBody(): Record<string, unknown> {
    const type = this.messageType();
    const override = this.recipientOverride().trim();
    const toField = override ? { to: override } : {};

    if (type === 'text') {
      return {
        type: 'text',
        contentText: this.draftMessage().trim(),
        ...toField,
      };
    }

    return {
      type: 'attachment',
      media: {
        kind: type,
        source: {
          source: 'url',
          mediaUrl: this.draftMediaUrl().trim(),
        },
        ...(this.draftMessage().trim() && { caption: this.draftMessage().trim() }),
      },
      ...toField,
    };
  }

  private async postConnectToBackend(authCode: string): Promise<void> {
    this.connecting.set(true);
    this.connectResult.set(null);

    const payload: Record<string, unknown> = {
      authCode,
      ...(this.wabaId() && { wabaId: this.wabaId() }),
      ...(this.phoneNumberId() && { phoneNumberId: this.phoneNumberId() }),
      ...(this.metaBusinessId() && { metaBusinessId: this.metaBusinessId() }),
    };

    try {
      const res = await fetch(`${environment.apiUrl}/v1/integrations/connect/meta/oauth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const body = await res.json();
      this.connectResult.set(JSON.stringify(body, null, 2));
    } catch (err) {
      this.connectResult.set(`Erro: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      this.connecting.set(false);
    }
  }

  private loadFbSdk(): void {
    const doc = this.document;
    if (doc.getElementById(FB_SDK_SCRIPT_ID)) {
      this.initFb();
      return;
    }

    window.fbAsyncInit = () => this.initFb();

    const script = doc.createElement('script');
    script.id = FB_SDK_SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';
    script.src = FB_SDK_SRC;
    doc.head.appendChild(script);

    this.destroyRef.onDestroy(() => {
      delete window.fbAsyncInit;
    });
  }

  private initFb(): void {
    const fb = window.FB;
    if (!fb) return;
    fb.init({
      appId: META_APP_ID,
      autoLogAppEvents: true,
      xfbml: true,
      version: FB_GRAPH_VERSION,
    });
    this.sdkReady.set(true);
  }

  private extractFinishField(key: string): string | null {
    for (const ev of this.sessionEvents()) {
      if (ev.parsed?.['event'] !== 'FINISH') continue;
      const data = ev.parsed?.['data'] as Record<string, unknown> | undefined;
      const val = data?.[key];
      if (typeof val === 'string') return val;
    }
    return null;
  }
}
